import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { RouteComponentProps } from "react-router-dom";
import { Form } from "react-final-form";
import { Box, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MUIFormSectionWFields } from "@upptic/module-ui";
import { SetUserPasswordMutation } from "v2/api/mutations/user/password";
import { ValidateUserToken } from "v2/api/queries/membership/validateMembershipActivationToken";
import { setCookie } from "react-use-cookie";

import config from "config";

import { useRequestErrorHandler } from "v2/hooks/useRequestErrorHandler";
import validators from "utils/validators";
import formatters from "utils/formatters";

import { Error, Spinner } from "components";
import { AuthSection, SignInWithGoogleButton } from "components/auth";
import ActivateAccountErrorPage from "screens/Auth/SignUpActivate/ActivateAccountErrorPage";
import { useSignIn } from "../../../hooks/useSignIn";
import OtpPage from "screens/Auth/SignIn/OtpPage";

interface Props
  extends RouteComponentProps<{
    token: string;
    isNewUser?: string;
  }> {}

export interface FormData {
  password: string;
  passwordConfirm: string;
  firstName: string;
  lastName: string;
}

function SignUpSetPasswordContainer({ match }: Props) {
  const { t: tg } = useTranslation("general");
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpInitalValues, setOtpInitalValues] = useState(false);
  const variables = {
    token: match.params.token,
    title: "Set password",
  };
  const [updatePassword] = useMutation(SetUserPasswordMutation);
  const [activationError, setActivationError] = useState(false);

  const signIn = useSignIn({
    // tslint:disable-next-line:no-shadowed-variable
    onOtpRequired: (data) => {
      setOtpInitalValues(data);
      setShowOtpDialog(true);
    },
  });

  const handleError = useRequestErrorHandler();

  const { data, error, loading } = useQuery(ValidateUserToken, {
    variables: { token: variables.token },
  });

  const setPassword = async (password: string) => {
    const updatePasswordResponse = await handleError(
      updatePassword({
        variables: {
          token: variables.token,
          password,
        },
      }),
    );

    if (!updatePasswordResponse) {
      setActivationError(true);
      return;
    }
    return updatePasswordResponse.data?.setUserPassword;
  };

  const handleSubmit = async (formData: FormData) => {
    const setPasswordResponse = await setPassword(formData.password);

    if (setPasswordResponse.emailAddress) {
      setCookie(config.lastLoginClientCookieName, "", { days: -1 });
      await signIn({
        email: setPasswordResponse?.emailAddress,
        password: formData.password,
      });
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <Error errorElement={error.message} />;
  }

  if (!data || data.validateUserToken !== "valid" || activationError) {
    return <ActivateAccountErrorPage />;
  }

  if (showOtpDialog) {
    return <OtpPage onSubmit={signIn} initialValues={otpInitalValues} />;
  }

  const defaultState: FormData = {
    password: "",
    passwordConfirm: "",
    firstName: "",
    lastName: "",
  };

  const fields = [
    {
      name: "password",
      label: "New Password",
      type: "password",
      placeholder: "Enter Your New Password",
      validate: validators.composeValidators(
        false,
        validators.required,
        validators.passwordLength,
        validators.passwordStrength,
      ),
      formatter: formatters.trim,
      xs: 12,
    },
    {
      name: "passwordConfirm",
      label: "Confirm New Password",
      type: "password",
      placeholder: "Repeat Your New Password",
      validate: validators.composeValidators(
        false,
        validators.required,
        (value: any, allValues: any) =>
          validators.passwordMatch(value, allValues.password),
      ),
      formatter: formatters.trim,
      xs: 12,
    },
  ];

  return (
    <Form onSubmit={handleSubmit} initialValues={defaultState}>
      {({ handleSubmit: submit, submitting }) => (
        <form onSubmit={submit} autoComplete="off">
          <AuthSection title={variables.title}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              width="100%"
            >
              <SignInWithGoogleButton />
              <span
                style={{
                  width: "100%",
                  textAlign: "center",
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                or
              </span>
              <MUIFormSectionWFields disablePadding fields={fields as any} />
              <Stack
                  sx={{ width: "100%" }}
                  spacing={2}
                  pt={2}
                  direction="row"
                  justifyContent="flex-end"
              >
                <Button
                  variant="contained"
                  sx={{ minWidth: "130px" }}
                  type="submit"
                  disabled={submitting}
                >
                  {tg<string>("submit")}
                </Button>
              </Stack>
            </Box>
          </AuthSection>
        </form>
      )}
    </Form>
  );
}

export default SignUpSetPasswordContainer;
